import React, { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import { playSound, SFX_KEYS } from '../game/systems/SoundManager';

interface SignatureMoveDefinition {
  id: string;
  baseName: string;
  description: string;
  category: 'offensive' | 'defensive' | 'utility' | 'support';
  element: 'flame' | 'storm' | 'tide' | 'shadow' | 'iron' | 'spirit';
  rarity: 'rare' | 'epic' | 'legendary';
  color: string;
}

interface EssenceConsumptionProps {
  essenceItemId: string;
  essenceName: string;
  essenceDescription: string;
  essenceRarity: 'rare' | 'epic' | 'legendary';
  unlocksMove: string;
  moveDefinition: SignatureMoveDefinition | null;
  socket: Socket | null;
  onClose: () => void;
  onSuccess: () => void;
}

const RARITY_COLORS: Record<string, string> = {
  rare: '#3b82f6',
  epic: '#a855f7',
  legendary: '#f59e0b',
};

const injectStyles = (): void => {
  const styleId = 'essence-consumption-style';
  if (document.getElementById(styleId)) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    .essence-modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.85);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 2000;
      backdrop-filter: blur(2px);
    }
    
    .essence-modal {
      background: #0a0a1a;
      border: 2px solid #1a1a2e;
      padding: 24px;
      max-width: 420px;
      width: 90%;
      font-family: 'Sometype Mono', monospace;
      box-shadow: 0 0 24px rgba(0, 0, 0, 0.9);
    }
    
    .essence-modal-title {
      color: #ffffff;
      font-size: 16px;
      font-weight: bold;
      margin-bottom: 16px;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    
    .essence-step-indicator {
      display: flex;
      gap: 8px;
      margin-bottom: 16px;
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    
    .essence-step {
      flex: 1;
      padding: 4px 6px;
      background: #111122;
      border: 1px solid #2a2a4e;
      text-align: center;
      color: #666;
    }
    
    .essence-step.active {
      background: #1a3a5f;
      border-color: #4fc3f7;
      color: #4fc3f7;
      font-weight: bold;
    }
    
    .essence-info {
      background: #111122;
      border: 2px solid #2a2a4e;
      padding: 12px;
      margin-bottom: 16px;
      position: relative;
    }
    
    .essence-name {
      color: #ffffff;
      font-size: 14px;
      font-weight: bold;
      margin-bottom: 4px;
    }
    
    .essence-description {
      color: #888;
      font-size: 11px;
      line-height: 1.4;
      margin-bottom: 8px;
    }
    
    .essence-rarity {
      font-size: 10px;
      padding: 3px 8px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      display: inline-block;
      font-weight: bold;
      border: 1px solid currentColor;
    }
    
    .move-preview {
      background: #111122;
      border: 1px solid #2a2a4e;
      padding: 12px;
      margin-bottom: 16px;
    }
    
    .move-preview-title {
      color: #888;
      font-size: 10px;
      text-transform: uppercase;
      margin-bottom: 8px;
      letter-spacing: 0.5px;
    }
    
    .move-preview-name {
      color: #ffffff;
      font-size: 13px;
      font-weight: bold;
      margin-bottom: 4px;
    }
    
    .move-preview-description {
      color: #888;
      font-size: 10px;
      line-height: 1.4;
      margin-bottom: 8px;
    }
    
    .move-preview-stats {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 6px;
      font-size: 9px;
    }
    
    .move-stat {
      background: #0a0a1a;
      padding: 4px 6px;
      border: 1px solid #1a1a2e;
    }
    
    .move-stat-label {
      color: #666;
      margin-bottom: 2px;
    }
    
    .move-stat-value {
      color: #fff;
      font-weight: bold;
    }
    
    .alias-input-container {
      margin-bottom: 16px;
    }
    
    .alias-label {
      color: #888;
      font-size: 10px;
      text-transform: uppercase;
      margin-bottom: 6px;
      display: block;
      letter-spacing: 0.5px;
    }
    
    .alias-input {
      width: 100%;
      padding: 8px 12px;
      font-size: 11px;
      font-family: 'Sometype Mono', monospace;
      background: #0a0a1a;
      border: 1px solid #2a2a4e;
      color: #fff;
      box-sizing: border-box;
    }
    
    .alias-input:focus {
      outline: none;
      border-color: #4fc3f7;
    }
    
    .essence-actions {
      display: flex;
      gap: 8px;
    }
    
    .essence-btn {
      flex: 1;
      padding: 10px 12px;
      font-size: 11px;
      font-family: 'Sometype Mono', monospace;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      cursor: pointer;
      border: 1px solid #2a2a4e;
      background: #1a1a2e;
      color: #888;
      font-weight: bold;
    }
    
    .essence-btn:hover {
      background: #2a2a4e;
      color: #fff;
    }
    
    .essence-btn.primary {
      background: #4fc3f7;
      border-color: #4fc3f7;
      color: #000;
    }
    
    .essence-btn.primary:hover {
      background: #29b6f6;
    }
    
    .essence-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `;
  document.head.appendChild(style);
};

export const EssenceConsumption: React.FC<EssenceConsumptionProps> = ({
  essenceItemId,
  essenceName,
  essenceDescription,
  essenceRarity,
  unlocksMove,
  moveDefinition,
  socket,
  onClose,
  onSuccess,
}) => {
  const [customAlias, setCustomAlias] = useState<string>('');
  const [isConsuming, setIsConsuming] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    injectStyles();
    if (moveDefinition) {
      setCustomAlias(moveDefinition.baseName);
    }
  }, [moveDefinition]);

  useEffect(() => {
    if (!socket) return;

    const handleUnlocked = (): void => {
      setIsConsuming(false);
      playSound(SFX_KEYS.ESSENCE_UNLOCK);
      onSuccess();
    };

    const handleError = (data: { message: string }): void => {
      setError(data.message);
      setIsConsuming(false);
    };

    socket.on('wildcard:unlocked', handleUnlocked);
    socket.on('wildcard:error', handleError);

    return () => {
      socket.off('wildcard:unlocked', handleUnlocked);
      socket.off('wildcard:error', handleError);
    };
  }, [socket, onSuccess]);

  const handleConsume = useCallback(() => {
    if (!socket || !customAlias.trim()) {
      setError('Please enter a name for your signature move');
      return;
    }

    setIsConsuming(true);
    setError(null);
    playSound(SFX_KEYS.ESSENCE_CONSUME);
    socket.emit('wildcard:consume', {
      essenceItemId,
      customAlias: customAlias.trim(),
    });
  }, [socket, essenceItemId, customAlias]);

  return (
    <div className="essence-modal-overlay" onClick={onClose}>
      <div className="essence-modal" onClick={e => e.stopPropagation()}>
        <div className="essence-modal-title">Unlock Signature Move</div>
        
        <div className="essence-step-indicator">
          <div className="essence-step active">Find Essence</div>
          <div className="essence-step active">Consume</div>
          <div className="essence-step">Equip</div>
          <div className="essence-step">Use</div>
        </div>

        <div className="essence-info">
          <div className="essence-name">{essenceName}</div>
          <div className="essence-description">{essenceDescription}</div>
          <span
            className="essence-rarity"
            style={{
              background: RARITY_COLORS[essenceRarity],
              color: essenceRarity === 'legendary' ? '#000' : '#fff',
            }}
          >
            {essenceRarity}
          </span>
        </div>

        {moveDefinition && (
          <div className="move-preview">
            <div className="move-preview-title">Unlocks Signature Move</div>
            <div className="move-preview-name" style={{ color: moveDefinition.color }}>
              {moveDefinition.baseName}
            </div>
            <div className="move-preview-description">
              {moveDefinition.description}
            </div>
            <div className="move-preview-stats">
              <div className="move-stat">
                <div className="move-stat-label">Category</div>
                <div className="move-stat-value">
                  {moveDefinition.category.charAt(0).toUpperCase() + moveDefinition.category.slice(1)}
                </div>
              </div>
              <div className="move-stat">
                <div className="move-stat-label">Element</div>
                <div className="move-stat-value">
                  {moveDefinition.element.charAt(0).toUpperCase() + moveDefinition.element.slice(1)}
                </div>
              </div>
              <div className="move-stat">
                <div className="move-stat-label">Base Damage</div>
                <div className="move-stat-value">{moveDefinition.baseName === 'Flame Burst' ? '50' : '70'}</div>
              </div>
              <div className="move-stat">
                <div className="move-stat-label">Cooldown</div>
                <div className="move-stat-value">8s</div>
              </div>
            </div>
          </div>
        )}

        <div className="alias-input-container">
          <label className="alias-label">Custom Name (Optional)</label>
          <input
            type="text"
            className="alias-input"
            value={customAlias}
            onChange={e => setCustomAlias(e.target.value)}
            placeholder="Enter a custom name for this move"
            maxLength={30}
            disabled={isConsuming}
          />
        </div>

        {error && (
          <div style={{ color: '#ef4444', fontSize: 11, marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div className="essence-actions">
          <button
            className="essence-btn"
            onClick={onClose}
            disabled={isConsuming}
          >
            Cancel
          </button>
          <button
            className="essence-btn primary"
            onClick={handleConsume}
            disabled={isConsuming || !customAlias.trim()}
          >
            {isConsuming ? 'Consuming...' : 'Consume'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EssenceConsumption;
